import { Button } from '@creditornot/cb-button';
import { Checkbox } from '@creditornot/cb-checkbox';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Select } from '@creditornot/cb-select';
import { Modal } from '@creditornot/cbt-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import * as brandApi from 'apis/restaurant-api/merchant-admin/brand/api';
import * as enterpriseApi from 'apis/restaurant-api/merchant-admin/enterprise/api';
import * as merchantApi from 'apis/restaurant-api/merchant-admin/merchant/api';
import * as venueApi from 'apis/restaurant-api/merchant-admin/venue/api';
import { APIErrorNotification } from 'common/components';
import { useRetailContext } from 'common/hooks';

import UserSelect, { User } from './UserSelect';
import { getUserRoleOptions } from './getUserRoleOptions';
import type { EntityType, Role } from './types';
import { isBrandRole, isEnterpriseRole, isMerchantRole, isVenueRole } from './utils';

type Props = {
  entityId: string;
  entityType: EntityType;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const SelectWrapper = styled.div`
  padding: 12px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const ErrorWrapper = styled.div`
  padding: 24px;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const Error = styled(APIErrorNotification)``;

const CancelButton = styled(Button).attrs({
  variant: 'lightBlue',
  size: 'small',
})``;

const AddUserButton = styled(Button).attrs({
  variant: 'blue',
  size: 'small',
})``;

const DisabledInput = styled(Input).attrs({
  disabled: true,
  readOnly: true,
})``;

const DisabledCheckbox = styled(Checkbox).attrs({
  disabled: true,
  readOnly: true,
})``;

const StyledFormRow = styled(FormRow).attrs({
  labelAlign: 'center',
  horizontalPadding: true,
})``;

const DirectAddModal = ({ isOpen, entityType, entityId, onClose, onSuccess }: Props) => {
  const intl = useIntl();
  const isRetailContext = useRetailContext();

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const resetSelections = () => {
    setSelectedUser(null);
    setSelectedRole(null);
  };

  const grantAccessMutation = useMutation(
    async ({ user, role }: { user: User; role: Role }) => {
      if (entityType === 'merchant' && isMerchantRole(role)) {
        return merchantApi.grantMerchantAccess(entityId, user.value, role);
      }

      if (entityType === 'venue' && isVenueRole(role)) {
        return venueApi.grantVenueAccess(entityId, user.value, role);
      }
      if (entityType === 'enterprise' && isEnterpriseRole(role)) {
        return enterpriseApi.grantEnterpriseAccess(entityId, user.value, role);
      }

      if (entityType === 'brand' && isBrandRole(role)) {
        return brandApi.grantBrandAccess(entityId, user.value, role);
      }

      return null;
    },
    {
      onSuccess: () => {
        onSuccess();
        resetSelections();
      },
    },
  );

  const handleClose = () => {
    resetSelections();
    onClose();
  };

  const addSelectedUser = () => {
    if (selectedUser === null || selectedRole === null) {
      return;
    }
    grantAccessMutation.mutate({
      role: selectedRole,
      user: selectedUser,
    });
  };

  const options = getUserRoleOptions(entityType, isRetailContext, intl);

  return (
    <Modal
      disableContentPadding
      open={isOpen}
      onDismiss={handleClose}
      title={intl.formatMessage({ id: 'userManagement.directAddUsers' })}
      sizePreset="centered:large"
      footer={
        <>
          <CancelButton onClick={handleClose} disabled={grantAccessMutation.isLoading}>
            <FormattedMessage id="userManagement.cancel" />
          </CancelButton>
          {selectedUser && (
            <AddUserButton
              onClick={addSelectedUser}
              disabled={!selectedRole || grantAccessMutation.isLoading}
              loading={grantAccessMutation.isLoading}
            >
              <FormattedMessage id="userManagement.addUser" />
            </AddUserButton>
          )}
        </>
      }
      footerStyle={{
        justifyContent: 'flex-end',
        gap: 16,
      }}
    >
      <SelectWrapper>
        <UserSelect
          entityId={entityId}
          entityType={entityType}
          onChange={setSelectedUser}
          menuPortalTarget={document.body}
        />
      </SelectWrapper>
      <StyledFormRow label={intl.formatMessage({ id: 'userManagement.name' })}>
        <DisabledInput value={selectedUser?.label ?? ''} />
      </StyledFormRow>
      <StyledFormRow label={intl.formatMessage({ id: 'userManagement.email' })}>
        <DisabledInput value={selectedUser?.email ?? ''} />
      </StyledFormRow>
      <StyledFormRow label={intl.formatMessage({ id: 'userManagement.emailVerified' })}>
        <DisabledCheckbox checked={!!selectedUser?.isEmailVerified} />
      </StyledFormRow>
      <StyledFormRow label={intl.formatMessage({ id: 'userManagement.selectRights' })}>
        <Select
          value={selectedRole}
          onChange={(value: Role | null) => {
            setSelectedRole(value);
          }}
          placeholder={intl.formatMessage({ id: 'userManagement.selectRolePrompt' })}
          options={options}
          menuPortalTarget={document.body}
        />
      </StyledFormRow>
      {grantAccessMutation.isError && (
        <ErrorWrapper>
          <Error error={grantAccessMutation.error} />
        </ErrorWrapper>
      )}
    </Modal>
  );
};

export default DirectAddModal;
